import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { BarChart3, Trash2, CalendarRange, Send } from 'lucide-react';

const Card = ({ icon: Icon, color, title, children, testId }) => (
  <div className="bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col gap-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}><Icon className="h-5 w-5 text-white" /></div>
      <h2 className="font-semibold">{title}</h2>
    </div>
    {children}
  </div>
);

export const SettingsCards = ({ onAuthError }) => {
  const { toast } = useToast();
  const [gaId, setGaId] = useState('');
  const [testLeads, setTestLeads] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [pub, tl, wk] = await Promise.all([adminApi.get('/settings/public'), adminApi.get('/settings/test-leads'), adminApi.get('/weekly/status')]);
      setGaId(pub.data.ga_id || ''); setTestLeads(tl.data); setWeekly(wk.data);
    } catch (err) { onAuthError(err); }
  }, [onAuthError]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okTitle) => {
    setBusy(key);
    try { const { data } = await fn(); toast({ title: okTitle, description: data.message }); load(); }
    catch (err) { if (!onAuthError(err)) toast({ title: 'Error', description: err.response?.data?.detail || 'Operación fallida.', variant: 'destructive' }); }
    finally { setBusy(''); }
  };

  return (
    <section className="grid lg:grid-cols-3 gap-4" data-testid="admin-settings">
      <Card icon={BarChart3} color="from-orange-500 to-amber-400" title="Google Analytics" testId="settings-ga-card">
        <p className="text-sm text-slate-400">Pega tu ID de medición (GA4) y empezamos a enviar visitas y páginas vistas.</p>
        <form onSubmit={(e) => { e.preventDefault(); run('ga', () => adminApi.put('/settings/analytics', { ga_id: gaId }), 'Guardado'); }} className="flex gap-2">
          <Input data-testid="settings-ga-input" value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" className="h-10 bg-slate-950 border-white/10 text-white rounded-xl" />
          <Button type="submit" disabled={busy === 'ga'} className="h-10 rounded-xl bg-blue-600 hover:bg-blue-500" data-testid="settings-ga-save">Guardar</Button>
        </form>
        <p className="text-xs text-slate-500" data-testid="settings-ga-status">{gaId ? 'Activo: se carga gtag.js en todas las páginas.' : 'Desactivado. Deja vacío para no medir.'}</p>
      </Card>

      <Card icon={Trash2} color="from-red-600 to-rose-400" title="Leads de prueba" testId="settings-testleads-card">
        <p className="text-sm text-slate-400">Elimina suscriptores de prueba (@example.com, @resend.dev, test_...) antes de salir a producción.</p>
        <p className="text-3xl font-bold" data-testid="settings-testleads-count">{testLeads ? testLeads.count : '—'}<span className="text-sm text-slate-400 font-normal ml-2">detectados</span></p>
        <Button data-testid="settings-testleads-delete" disabled={!testLeads?.count || busy === 'tl'} onClick={() => window.confirm(`¿Eliminar ${testLeads.count} leads de prueba?\n\n${testLeads.emails.join('\n')}`) && run('tl', () => adminApi.delete('/settings/test-leads'), 'Leads eliminados')} className="rounded-xl bg-red-600 hover:bg-red-500">
          <Trash2 className="h-4 w-4 mr-2" /> Eliminar leads de prueba
        </Button>
      </Card>

      <Card icon={CalendarRange} color="from-blue-600 to-cyan-400" title="Resumen semanal" testId="settings-weekly-card">
        <p className="text-sm text-slate-400">Cada <span className="text-white">lunes</span> (a la hora del recordatorio) recibes leads nuevos, clics por aliado y artículos más leídos.</p>
        {weekly && (
          <p className="text-xs text-slate-400" data-testid="settings-weekly-preview">
            Esta semana: <span className="text-white">{weekly.preview.leads}</span> leads · <span className="text-white">{weekly.preview.clicks}</span> clics · <span className="text-white">{weekly.preview.comparador}</span> comparaciones
            <br />Último envío: <span className="text-slate-200" data-testid="settings-weekly-last">{weekly.last_sent ? new Date(weekly.last_sent.sent_at + 'Z').toLocaleString('es-VE') : 'Nunca'}</span>
          </p>
        )}
        <Button data-testid="settings-weekly-send" disabled={busy === 'wk'} onClick={() => run('wk', () => adminApi.post('/weekly/send-now'), 'Resumen enviado')} className="rounded-xl bg-blue-600 hover:bg-blue-500">
          <Send className="h-4 w-4 mr-2" /> {busy === 'wk' ? 'Enviando...' : 'Enviarme ahora'}
        </Button>
      </Card>
    </section>
  );
};

export default SettingsCards;
